import { z } from "zod";

/** Hard ceilings keep replay bounded inside the Worker's CPU budget. */
export const PROOF_LIMITS = {
  maxSortingActions: 400,
  /** Color Lab proofs: pours, undos and hints (the client folds undone pours away past this). */
  maxColorMoves: 500,
  /** Tháp Bi Lật proofs: drops, undos and hints (the client folds undone drops away past this). */
  maxLogicMoves: 500,
  /** Lối Ngọc proofs: steps, undos and hints (the client folds undone steps away past this). */
  maxTileMoves: 600,
  /** Xưởng Phép Thuật proofs: rolls, undos and hints (the client folds undone rolls away past this). */
  maxFillMoves: 400,
  /** Máy Gắp Lém Lỉnh proofs: coins, undos and hints (the client folds undone coins away past this). */
  maxArcadeMoves: 500,
  maxElapsedMs: 6 * 60 * 60 * 1000,
  maxHints: 20,
} as const;

const idSchema = z.string().min(1).max(32).regex(/^[a-z0-9_-]+$/i);
const seqSchema = z.number().int().min(1).max(PROOF_LIMITS.maxSortingActions);
const elapsedSchema = z.number().int().min(0).max(PROOF_LIMITS.maxElapsedMs);

export const sortDropSchema = z
  .object({
    seq: seqSchema,
    kind: z.literal("drop"),
    itemId: idSchema,
    toBinId: idSchema,
    clientElapsedMs: elapsedSchema,
  })
  .strict();

export const sortUndoSchema = z
  .object({
    seq: seqSchema,
    kind: z.literal("undo"),
    clientElapsedMs: elapsedSchema,
  })
  .strict();

export const sortHintSchema = z
  .object({
    seq: seqSchema,
    kind: z.literal("hint"),
    clientElapsedMs: elapsedSchema,
  })
  .strict();

export const sortMoveSchema = z.discriminatedUnion("kind", [
  sortDropSchema,
  sortUndoSchema,
  sortHintSchema,
]);
export type SortMove = z.infer<typeof sortMoveSchema>;

export const sortingProofSchema = z
  .object({
    houseId: z.literal("sorting"),
    levelId: z.number().int().min(1).max(100),
    contentVersion: z.string().min(1).max(32),
    moves: z.array(sortMoveSchema).min(1).max(PROOF_LIMITS.maxSortingActions),
  })
  .strict();
export type SortingProof = z.infer<typeof sortingProofSchema>;

const colorSeqSchema = z.number().int().min(1).max(PROOF_LIMITS.maxColorMoves);

export const colorPourSchema = z
  .object({
    seq: colorSeqSchema,
    kind: z.literal("pour"),
    from: idSchema,
    to: idSchema,
    clientElapsedMs: elapsedSchema,
  })
  .strict();

export const colorUndoSchema = z.object({ seq: colorSeqSchema, kind: z.literal("undo"), clientElapsedMs: elapsedSchema }).strict();
export const colorHintSchema = z.object({ seq: colorSeqSchema, kind: z.literal("hint"), clientElapsedMs: elapsedSchema }).strict();

export const colorMoveSchema = z.discriminatedUnion("kind", [colorPourSchema, colorUndoSchema, colorHintSchema]);
export type ColorMove = z.infer<typeof colorMoveSchema>;

export const colorsProofSchema = z
  .object({
    houseId: z.literal("colors"),
    levelId: z.number().int().min(1).max(100),
    contentVersion: z.string().min(1).max(32),
    moves: z.array(colorMoveSchema).min(1).max(PROOF_LIMITS.maxColorMoves),
  })
  .strict();
export type ColorsProof = z.infer<typeof colorsProofSchema>;

const logicSeqSchema = z.number().int().min(1).max(PROOF_LIMITS.maxLogicMoves);

/** Tháp Bi Lật: the front marble of a hopper dropped down a column of the tower. */
export const logicDropSchema = z
  .object({
    seq: logicSeqSchema,
    kind: z.literal("drop"),
    hopper: z.number().int().min(0).max(3),
    column: z.number().int().min(0).max(7),
    clientElapsedMs: elapsedSchema,
  })
  .strict();

export const logicUndoSchema = z.object({ seq: logicSeqSchema, kind: z.literal("undo"), clientElapsedMs: elapsedSchema }).strict();
export const logicHintSchema = z.object({ seq: logicSeqSchema, kind: z.literal("hint"), clientElapsedMs: elapsedSchema }).strict();

export const logicMoveSchema = z.discriminatedUnion("kind", [logicDropSchema, logicUndoSchema, logicHintSchema]);
export type LogicMove = z.infer<typeof logicMoveSchema>;

export const logicProofSchema = z
  .object({
    houseId: z.literal("logic"),
    levelId: z.number().int().min(1).max(100),
    contentVersion: z.string().min(1).max(32),
    moves: z.array(logicMoveSchema).min(1).max(PROOF_LIMITS.maxLogicMoves),
  })
  .strict();
export type LogicProof = z.infer<typeof logicProofSchema>;

const tileSeqSchema = z.number().int().min(1).max(PROOF_LIMITS.maxTileMoves);

/** Lối Ngọc: a step onto a neighbouring cell ("x-y"), walks across slabs recorded step by step. */
export const tileStepSchema = z
  .object({
    seq: tileSeqSchema,
    kind: z.literal("step"),
    to: idSchema,
    clientElapsedMs: elapsedSchema,
  })
  .strict();

export const tileUndoSchema = z.object({ seq: tileSeqSchema, kind: z.literal("undo"), clientElapsedMs: elapsedSchema }).strict();
export const tileHintSchema = z.object({ seq: tileSeqSchema, kind: z.literal("hint"), clientElapsedMs: elapsedSchema }).strict();

export const tileMoveSchema = z.discriminatedUnion("kind", [tileStepSchema, tileUndoSchema, tileHintSchema]);
export type TileMove = z.infer<typeof tileMoveSchema>;

export const tilesProofSchema = z
  .object({
    houseId: z.literal("tiles"),
    levelId: z.number().int().min(1).max(100),
    contentVersion: z.string().min(1).max(32),
    moves: z.array(tileMoveSchema).min(1).max(PROOF_LIMITS.maxTileMoves),
  })
  .strict();
export type TilesProof = z.infer<typeof tilesProofSchema>;

const fillSeqSchema = z.number().int().min(1).max(PROOF_LIMITS.maxFillMoves);

/** Xưởng Phép Thuật: a roll from the shelf ("R", "D3", "H") set down on a slot of the box (row-major). */
export const fillUnrollSchema = z
  .object({
    seq: fillSeqSchema,
    kind: z.literal("unroll"),
    roll: z.string().regex(/^(?:[RLDU][2-5]?|[HV])$/),
    cell: z.number().int().min(0).max(48),
    clientElapsedMs: elapsedSchema,
  })
  .strict();

export const fillUndoSchema = z.object({ seq: fillSeqSchema, kind: z.literal("undo"), clientElapsedMs: elapsedSchema }).strict();
export const fillHintSchema = z.object({ seq: fillSeqSchema, kind: z.literal("hint"), clientElapsedMs: elapsedSchema }).strict();

export const fillMoveSchema = z.discriminatedUnion("kind", [fillUnrollSchema, fillUndoSchema, fillHintSchema]);
export type FillMove = z.infer<typeof fillMoveSchema>;

export const fillProofSchema = z
  .object({
    houseId: z.literal("fill"),
    levelId: z.number().int().min(1).max(100),
    contentVersion: z.string().min(1).max(32),
    moves: z.array(fillMoveSchema).min(1).max(PROOF_LIMITS.maxFillMoves),
  })
  .strict();
export type FillProof = z.infer<typeof fillProofSchema>;

const arcadeSeqSchema = z.number().int().min(1).max(PROOF_LIMITS.maxArcadeMoves);

/** Máy Gắp Lém Lỉnh: one coin spent on a column of the claw machine. */
export const arcadeGrabSchema = z
  .object({
    seq: arcadeSeqSchema,
    kind: z.literal("grab"),
    column: z.number().int().min(0).max(7),
    clientElapsedMs: elapsedSchema,
  })
  .strict();

export const arcadeUndoSchema = z.object({ seq: arcadeSeqSchema, kind: z.literal("undo"), clientElapsedMs: elapsedSchema }).strict();
export const arcadeHintSchema = z.object({ seq: arcadeSeqSchema, kind: z.literal("hint"), clientElapsedMs: elapsedSchema }).strict();

export const arcadeMoveSchema = z.discriminatedUnion("kind", [arcadeGrabSchema, arcadeUndoSchema, arcadeHintSchema]);
export type ArcadeMove = z.infer<typeof arcadeMoveSchema>;

export const arcadeProofSchema = z
  .object({
    houseId: z.literal("arcade"),
    levelId: z.number().int().min(1).max(100),
    contentVersion: z.string().min(1).max(32),
    moves: z.array(arcadeMoveSchema).min(1).max(PROOF_LIMITS.maxArcadeMoves),
  })
  .strict();
export type ArcadeProof = z.infer<typeof arcadeProofSchema>;

/** Each house adds its own proof schema here when it opens. */
export const levelProofSchema = z.discriminatedUnion("houseId", [sortingProofSchema, colorsProofSchema, tilesProofSchema, logicProofSchema, fillProofSchema, arcadeProofSchema]);
export type LevelProof = z.infer<typeof levelProofSchema>;
